export type ElementPosition = 'center' | 'left' | 'right'

export function getCoordinates(
  element: HTMLElement,
  position: ElementPosition = 'center'
): { x: number; y: number } {
  const rect = element.getBoundingClientRect()
  const y = rect.top + rect.height / 2

  let x: number
  switch (position) {
    case 'center':
      x = rect.left + rect.width / 2
      break
    case 'left':
      x = rect.left + rect.width * 0.2
      break
    case 'right':
      x = rect.left + rect.width * 0.8
      break
  }

  return { x, y }
}

export async function scrollIntoView(element: HTMLElement): Promise<void> {
  element.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
    inline: 'center',
  })

  let prevPos = getCoordinates(element)

  while (true) {
    await new Promise<void>((resolve) => setTimeout(resolve, 100))

    const currentPos = getCoordinates(element)
    if (currentPos.x === prevPos.x && currentPos.y === prevPos.y) break

    prevPos = currentPos
  }
}

export function dispatchClick(element: HTMLElement): void {
  const elementPos = getCoordinates(element)

  const pointerOptions: PointerEventInit = {
    bubbles: true,
    cancelable: true,
    clientX: elementPos.x,
    clientY: elementPos.y,
    button: 0,
    pointerId: 1,
    pointerType: 'mouse',
    isPrimary: true,
  }

  element.dispatchEvent(new PointerEvent('pointerdown', { ...pointerOptions, buttons: 1 }))
  element.dispatchEvent(new PointerEvent('pointerup', { ...pointerOptions, buttons: 0 }))
  element.dispatchEvent(
    new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      clientX: elementPos.x,
      clientY: elementPos.y,
    })
  )
}

function setElementValue(element: HTMLInputElement, value: string): void {
  Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set?.call(element, value)
}

export async function dispatchType(element: HTMLInputElement, text: string): Promise<void> {
  setElementValue(element, '')
  for (const char of text) {
    setElementValue(element, element.value + char)
    element.dispatchEvent(
      new InputEvent('input', {
        bubbles: true,
        cancelable: true,
        inputType: 'insertText',
        data: char,
      })
    )
    await new Promise<void>((resolve) => setTimeout(resolve, 50))
  }
}

export async function dispatchSelect(element: HTMLSelectElement, option: string): Promise<void> {
  const options = Array.from(element.options)
  const matched = options.find((opt) => opt.textContent?.includes(option))
  if (!matched) {
    throw new Error(`Option not found: "${option}"`)
  }
  element.value = matched.value
  element.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }))
}

const userEventTypes = [
  'click',
  'mousedown',
  'mouseup',
  'mousemove',
  'wheel',
  'keydown',
  'keyup',
  'pointerdown',
  'pointerup',
  'pointermove',
  'pointercancel',
  'touchstart',
  'touchend',
  'touchmove',
  'touchcancel',
]

function blockUserInput(e: Event): void {
  e.preventDefault()
  e.stopPropagation()
}

export function blockUserInteractionOn(element: HTMLElement): void {
  const options = { capture: true }
  for (const type of userEventTypes) {
    element.addEventListener(type, blockUserInput, options)
  }
}
